import { useState, useEffect } from 'react';
import axios from 'axios';
import SelectSearchInput from '../SelectSearch';
import Navbar from '../Navbar';
import Footer from '../Footer';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import InventoryCalculationTable from './InventoryCalculationTable';

const InventoryCalculation = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const [token, setToken] = useState(sessionStorage.getItem('token') || "");
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const today = new Date().toISOString().split("T")[0];
    const navigate = useNavigate();

    const [consumptionData, setConsumptionData] = useState(null);
    const [inventoryData, setInventoryData] = useState([]);
    const [itemDetails, setItemDetails] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('All');
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [startPeriod, setStartPeriod] = useState('');
    const [endPeriod, setEndPeriod] = useState('');
    const [itemSearchTerm, setItemSearchTerm] = useState("");
    const [groupSearchTerm, setGroupSearchTerm] = useState("");
    const [includeSubRecipe, setIncludeSubRecipe] = useState(false);

    const fetchDates = async () => {
        if (!token || !selectedOutlet) return;

        try {
            setLoading(true);
            const response = await axios.post(`${baseUrl}/get-inventory-period`, {
                token: token,
                outlet: selectedOutlet
            });
            const data = response.data;
            setStartPeriod(data?.opening_date);
            setEndPeriod(data?.closing_date);

            const isoStart = formatToISODate(data?.opening_date);
            const isoEnd = formatToISODate(data?.closing_date);

            setStartDate(isoStart);
            setEndDate(isoEnd);
            setError(null);
            handleSearch(isoStart, isoEnd);
        } catch (err) {
            setError(err.message);
            toast.error("Failed to fetch dates");
        }
    }

    useEffect(() => {
        fetchDates();
    }, [token, selectedOutlet, startPeriod, endPeriod])

    const handleSearch = async (searchStartDate, searchEndDate) => {
        if (!token || !selectedOutlet || !searchStartDate || !searchEndDate) return;

        try {
            setLoading(true);
            const response = await axios.post(`${baseUrl}/menu-consumption`, {
                startDate: searchStartDate || startDate,
                endDate: searchEndDate || endDate,
                token: token,
                includeSubRecipeItems: !includeSubRecipe,
                outlet: selectedOutlet
            });
            const data = response.data;
            setConsumptionData(data);
            setError(null);
        } catch (err) {
            setError(err.message);
            setConsumptionData(null);
            toast.error("Failed to fetch data");
        } finally {
            setLoading(false);
        }

        fetchInventoryData();
    };

    const fetchInventoryData = async () => {
        if (!token || !selectedOutlet) return;

        if (!endDate || !startPeriod) return;

        try {
            setLoading(true);
            const response = await axios.post(`${baseUrl}/inventory-summary`, {
                token: token,
                includeSubRecipeItems: true,
                outlet: selectedOutlet,
                startDate: formatToISODate(startPeriod),
                endDate: endDate
            });
            const data = response.data;
            setInventoryData(data);
            setError(null);
        } catch (err) {
            setError(err.message);
            setInventoryData([]);
            toast.error("Failed to fetch data");
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (e, type) => {
        const value = e.target.value;
        if (type === 'start') {
            setStartDate(value);
        } else {
            setEndDate(value);
        }
    };

    const [savedPhysicalCounts, setSavedPhysicalCounts] = useState(() => {
        try {
            const raw = localStorage.getItem('savedPhysicalCount');
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });

    const getPhysicalFromSaved = (itemName) => {
        const match = savedPhysicalCounts.find(entry => entry.item_name === itemName);
        return match ? match.quantity : undefined;
    };

    const areAllCountsEmpty = () => {
        return savedPhysicalCounts.every(entry => {
            const qty = Number(entry?.quantity);
            return qty === 0 || isNaN(qty);
        });
    };

    const fetchItemData = async (item) => {
        try {
            const response = await axios.post(`${baseUrl}/get-menu-item`, {
                token: token,
                restaurant: selectedOutlet,
                name: item.ItemName
            })

            const data = response.data || [];
            sessionStorage.setItem('item', data.Name);
            sessionStorage.setItem('group', data.Description);
            sessionStorage.setItem('outlet', selectedOutlet)
            navigate('/itemdata');
        } catch (error) {
            console.log(error);
        }
    }

    const handleFetchDetails = async (item) => {
        try {
            const response = await axios.post(`${baseUrl}/itemconsumefromsale`, {
                token: token,
                outlet: selectedOutlet,
                item_name: item.name,
                startDate: formatToISODate(startPeriod),
                endDate: endDate
            })

            const data = response.data || {};
            setItemDetails(data);
            setShowModal(true);
        } catch (error) {
            console.log(error);
            toast.error('Failed to fetch details');
        }
    }

    const handleSave = () => {
        if (startDate !== formatToISODate(startPeriod)) {
            toast.error('Both start and end dates must match with closing summary');
            return;
        }
        handleSubmit();
    };

    const handleSubmit = () => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save inventory data?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();
                            if (!token || !selectedOutlet || !inventoryData?.summary) return;

                            try {
                                setLoading(true);

                                const items = Object.entries(inventoryData.summary)
                                    .flatMap(([category, groups]) =>
                                        Object.entries(groups).flatMap(([groupName, items]) =>
                                            items.map(item => {
                                                const physicalCount = Number(getPhysicalFromSaved(item.name));
                                                const closingBalance = Number(item.closing_balance);

                                                return {
                                                    name: item.name,
                                                    type: category,
                                                    group_name: groupName,
                                                    uom: item.uom,
                                                    new_uom: item.new_uom || item.uom,
                                                    total_consumed: item.total_consumed || 0,
                                                    total_received: item.total_received || 0,
                                                    closing_balance: closingBalance,
                                                    physical_count: physicalCount || closingBalance,
                                                    opening_count: item.opening || 0,
                                                    total_wastage: item.total_wasted || 0,
                                                    discrepancy_count:
                                                        physicalCount > 0 ? physicalCount - closingBalance : 0
                                                };
                                            })
                                        )
                                    );

                                const payload = {
                                    token: token,
                                    outlet: selectedOutlet,
                                    start_date: startPeriod,
                                    end_date: endPeriod,
                                    items: items,
                                    physical_added: !areAllCountsEmpty(),
                                    food_accuracy: foodPercentage,
                                    food_total_items: totalFoodItems,
                                    beverage_accuracy: beveragePercentage,
                                    beverage_total_items: totalBeverageItems
                                };

                                await axios.post(`${baseUrl}/save-inventory-snapshot`, payload);
                                localStorage.removeItem('savedPhysicalCount');
                                setSavedPhysicalCounts([]);
                                setInventoryData([]);
                                fetchDates();
                                toast.success("Inventory snapshot saved successfully");
                            } catch (err) {
                                console.error("Error saving inventory:", err);
                                const errorMessage = err?.response?.data?.error || err?.error || "Failed to save inventory snapshot";
                                toast.error(errorMessage);
                            }
                            finally {
                                setLoading(false);
                            }
                        }}
                    >
                        Save
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'save-inventory-data'
            }
        );
    }

    const formatToISODate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
    };

    const handleReceivedClick = () => {
        navigate('/inventoryreceived');
    };

    const handleOpeningClick = () => {
        navigate('/addopeningcount', {
            state: {
                inventoryData,
                selectedOutlet,
                accessToken: "a7fK2pX9Zd"
            }
        });
    };

    const handleWastageClick = () => {
        navigate('/addwastage');
    };

    const handlePhysicalClick = () => {
        navigate('/addphysicalcount', {
            state: {
                inventoryData,
                selectedOutlet,
                accessToken: "a7fK2pX9Zd"
            }
        });
    }

    const areAllOpeningCountsZero = (summary) => {
        if (!summary || Object.keys(summary).length === 0) return false;

        return Object.entries(summary).every(([category, groups]) =>
            Object.entries(groups).every(([groupName, items]) =>
                items.every(item => (item.opening ?? 0) === 0)
            )
        );
    };
    const allZero = areAllOpeningCountsZero(inventoryData.summary);

    const totalFoodItems = consumptionData?.grouped_by_item_type?.Food?.unique_items_sold || 0;
    const foodRecipeCount = consumptionData?.grouped_by_item_type?.Food?.itemwise_summary?.filter(item => !item?.has_recipe).length || 0;
    const foodPercentage = totalFoodItems > 0
        ? ((totalFoodItems - foodRecipeCount) * 100 / totalFoodItems).toFixed(2)
        : 0;

    const totalBeverageItems = consumptionData?.grouped_by_item_type?.Beverage?.unique_items_sold || 0;
    const beverageRecipeCount = consumptionData?.grouped_by_item_type?.Beverage?.itemwise_summary?.filter(item => !item?.has_recipe).length || 0;
    const beveragePercentage = totalBeverageItems > 0
        ? ((totalBeverageItems - beverageRecipeCount) * 100 / totalBeverageItems).toFixed(2)
        : 0;

    return (
        <div className='flex flex-col min-h-screen'>
            <Navbar />

            <div className='flex-grow p-4'>
                <div className="flex justify-center text-center mb-4">
                    <SelectSearchInput
                        token={token}
                        setToken={setToken}
                        selectedOutlet={selectedOutlet}
                        setSelectedOutlet={setSelectedOutlet}
                    />
                </div>

                <div className='flex items-center gap-20'>
                    <div className="flex items-center gap-3">
                        <span>From:</span>
                        <input
                            type="date"
                            className="border p-1 rounded"
                            value={startDate}
                            onChange={(e) => handleDateChange(e, 'start')}
                        />

                        <span>To:</span>
                        <input
                            type="date"
                            className="border p-1 rounded"
                            value={endDate}
                            onChange={(e) => handleDateChange(e, 'end')}
                        />
                    </div>

                    <div className='flex items-center gap-2'>
                        <input
                            type="checkbox"
                            checked={includeSubRecipe}
                            onChange={() => setIncludeSubRecipe(!includeSubRecipe)}
                        />
                        <label>Include Sub-recipe?</label>
                    </div>

                    <button
                        onClick={() => {
                            handleSearch(startDate, endDate);
                        }}
                        className="border-2 border-slate-300 hover:bg-slate-100 bg-slate-700 text-zinc-100 hover:text-zinc-700 rounded-lg flex items-center justify-center px-4 py-2 text-sm"
                    >
                        Search
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-4">
                        <svg className="animate-spin h-5 w-5 mr-2 inline-block text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                        </svg>
                        Loading data...
                    </div>
                ) : error ? (
                    <div className="text-red-500 py-4">Error: {error}</div>
                ) : consumptionData && (
                    <InventoryCalculationTable
                        consumptionData={consumptionData}
                        fetchItemData={fetchItemData}
                        itemSearchTerm={itemSearchTerm}
                        setItemSearchTerm={setItemSearchTerm}
                        filter={filter}
                        setFilter={setFilter}
                        groupSearchTerm={groupSearchTerm}
                        setGroupSearchTerm={setGroupSearchTerm}
                        handleReceivedClick={handleReceivedClick}
                        inventoryData={inventoryData}
                        handleSave={handleSave}
                        handleOpeningClick={handleOpeningClick}
                        allZero={allZero}
                        handleWastageClick={handleWastageClick}
                        handleFetchDetails={handleFetchDetails}
                        itemDetails={itemDetails}
                        showModal={showModal}
                        setShowModal={setShowModal}
                        foodPercentage={foodPercentage}
                        foodRecipeCount={foodRecipeCount}
                        totalFoodItems={totalFoodItems}
                        beveragePercentage={beveragePercentage}
                        beverageRecipeCount={beverageRecipeCount}
                        totalBeverageItems={totalBeverageItems}
                        handlePhysicalClick={handlePhysicalClick}
                        getPhysicalFromSaved={getPhysicalFromSaved}
                    />
                )}
            </div>

            <Footer />
        </div>
    );
};

export default InventoryCalculation;